<template>
  <Card interactive>
    <CardMedia
      :src="optimizedImageUrl"
      :to="contentUrl"
      :alt="$t('content.alt')"
      :aspect="CardAspect.RATIO_3_2"
      :fallback-icon-name="IconName.FILE_IMAGE"
    >
      <template #top-left>
        <ContentTypeIcon :content-type="content.type" />
      </template>
      <template #top-right>
        <Badge
          v-if="content.isCustomizable"
          :label="$t('content.customizable')"
          :color="BadgeColor.INFO"
        />
      </template>
    </CardMedia>

    <CardEyebrow :label="$t('search.type-label.content')" />

    <CardTitle :title="content.title" :to="contentUrl" />

    <CardSubtitle :text="content.description" />

    <CardActions>
      <template #start>
        <Button
          :label="$t('hub.explore')"
          :size="ButtonSize.SM"
          :to="contentUrl"
        />
      </template>
      <template #end>
        <ScormStatusChip
          v-if="
            content.type === ContentType.SCORM_COURSE &&
            content.scormProgressData
          "
          :status="content.scormProgressData.status"
          :latest-score="content.scormProgressData.latestScore"
        />
        <LaunchMetricChip :content="content" />
      </template>
    </CardActions>
  </Card>
</template>

<script setup lang="ts">
import {
  Badge,
  BadgeColor,
  Button,
  ButtonSize,
  Card,
  CardActions,
  CardAspect,
  CardEyebrow,
  CardMedia,
  CardSubtitle,
  CardTitle,
  IconName,
} from '@exfluential/ui';
import { ContentTypeIcon } from '~/components/content-type-icon';
import { ContentDto, ContentType } from '~/api/contents';
import { useOrganizationDomain } from '~/composables/use-organization-domain';
import { useOrganizationStore } from '~/store/organization';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRoute } from 'nuxt/app';
import imageProxyBuilder, { ResizeType } from '@bitpatty/imgproxy-url-builder';
import { useRuntimeConfig } from '#imports';
import { LaunchMetricChip } from '~/pages/_components/content-card/_components/launch-metric-chip';
import { ScormStatusChip } from './_components/scorm-status-chip';

type Props = {
  content: ContentDto;
  contentUrl?: string;
};

const props = defineProps<Props>();

const config = useRuntimeConfig();
const route = useRoute();
const organizationDomain = useOrganizationDomain();
const { organization } = storeToRefs(useOrganizationStore());
const hubSlug = computed<string>(() => route.params.hubSlug as string);

const contentUrl = computed(() => {
  if (props.contentUrl) {
    return props.contentUrl;
  }

  if (organizationDomain !== null || !organization.value) {
    return `/${hubSlug.value}/${props.content.slug}`;
  }
  return `/org/${organization.value.slug}/${hubSlug.value}/${props.content.slug}`;
});

const optimizedImageUrl = computed(() =>
  imageProxyBuilder()
    .resize({ type: ResizeType.FILL_DOWN, width: 480, height: 320 })
    .format('webp')
    .build({
      baseUrl: config.public.imgProxyBaseUrl || '',
      path: props.content.imageUrl,
      addExtension: true,
    }),
);
</script>
